import { readString } from "react-papaparse";

export type CsvValidationError = {
  line: number;
  key?: string;
  type: string;
  message: string;
};

function getCsvErrors(
  rows: { key: string; value: string }[],
  reference: Record<string, string>
): CsvValidationError[] {
  const errors: CsvValidationError[] = [];
  const seenKeys = new Set<string>();

  rows.forEach((row, index) => {
    const line = index + 2; // header = line 1
    const { key, value } = row;

    // 1. key checks
    if (!key) {
      errors.push({
        line,
        type: "EMPTY_KEY",
        message: "Empty key",
      });
      return;
    }

    if (seenKeys.has(key)) {
      errors.push({
        line,
        key,
        type: "DUPLICATE_KEY",
        message: `Duplicate key "${key}"`,
      });
    }
    seenKeys.add(key);

    // 2. empty value
    if (!value || !value.trim()) {
      errors.push({
        line,
        key,
        type: "EMPTY_VALUE",
        message: `Empty translation for "${key}"`,
      });
    }

    // 3. {{ }} balance
    const open = (value.match(/{{/g) || []).length;
    const close = (value.match(/}}/g) || []).length;

    if (open > 1 || close > 1) {
      errors.push({
        line,
        key,
        type: "INVALID_INTERPOLATION",
        message: `${value}, open: ${open}, close: ${close}`,
      });
    }
    if (open !== close) {
      errors.push({
        line,
        key,
        type: "INVALID_INTERPOLATION",
        message: `Unbalanced {{ }} in "${value}"`,
      });
    }

    // 4. placeholder consistency vs reference
    const ref = reference[key];
    if (ref) {
      const refVars = [...ref.matchAll(/{{\s*(\w+)\s*}}/g)].map((m) => m[1]);
      const valVars = [...value.matchAll(/{{\s*(\w+)\s*}}/g)].map((m) => m[1]);

      const missing = refVars.filter((v) => !valVars.includes(v));
      const extra = valVars.filter((v) => !refVars.includes(v));

      if (missing.length || extra.length) {
        errors.push({
          line,
          key,
          type: "PLACEHOLDER_MISMATCH",
          message: `Missing: [${missing.join(", ")}], Extra: [${extra.join(
            ", "
          )}]`,
        });
      }
    }
  });

  return errors;
}

// check CSV consistency
export const checkCsvConsistency = (
  file: File
): Promise<{
  errors: CsvValidationError[];
}> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;

      // Parse CSV using react-papaparse
      readString(text, {
        header: true,
        transform: (value) => value.trim(),
        complete: (results) => {
          // Convert to the expected format { key, value }
          const rows = results.data
            .map((row: any) => ({
              key: row.originalString,
              value: row.translation,
            }))
            .filter((row: any) => row.key || row.value);

          const errors = getCsvErrors(rows, {});
          resolve({ errors });
        },
        error: (error) => {
          reject(new Error(`CSV Parse Error: ${error.message}`));
        },
      });
    };
    reader.onerror = () => reject(new Error("Failed to read file"));
    reader.readAsText(file);
  });
};
